import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import MobileCTABar from "@/components/site/MobileCTABar";
import {
  getPostBySlug,
  PUBLISHED_POSTS,
  formatPostDate,
  getPostImage,
} from "@/lib/blog-posts";

function PostBody({ blocks }) {
  return (
    <div className="prose prose-slate max-w-none">
      {blocks.map((b, i) => {
        const k = `${b.type}-${i}-${b.text.slice(0, 24)}`;
        if (b.type === "h2") {
          return (
            <h2
              key={k}
              className="font-display text-2xl sm:text-3xl font-bold text-brand-navy mt-10 mb-3 leading-tight"
            >
              {b.text}
            </h2>
          );
        }
        return (
          <p
            key={k}
            className="text-base sm:text-lg text-slate-700 leading-relaxed mb-5"
          >
            {b.text}
          </p>
        );
      })}
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="pt-32 pb-24 max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h1 className="font-display text-3xl font-bold text-brand-navy">
            Post not found
          </h1>
          <p className="mt-3 text-slate-600">
            The post you're looking for might have moved.
          </p>
          <Button asChild className="mt-6 rounded-full">
            <Link to="/blog">Back to blog</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const img = getPostImage(post);
  const related = PUBLISHED_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 lg:pt-36 pb-24">
        {/* Back */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:text-brand-navy"
          >
            <ArrowLeft className="h-4 w-4" /> All posts
          </Link>
        </div>

        {/* Header */}
        <header className="max-w-3xl mx-auto px-5 sm:px-8 mt-6">
          <span className="inline-flex rounded-full bg-brand-lightblue text-brand-navy text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1">
            {post.category}
          </span>
          <h1
            data-testid="blog-post-title"
            className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-navy leading-[1.08]"
          >
            {post.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" /> {formatPostDate(post.date)}
            </span>
            {post.readingMinutes && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> {post.readingMinutes} min read
              </span>
            )}
            <span className="text-slate-300">·</span>
            <span>By {post.author}</span>
          </div>
        </header>

        {/* Cover */}
        {img && (
          <div className="max-w-4xl mx-auto px-5 sm:px-8 mt-8">
            <div className="rounded-3xl overflow-hidden aspect-[16/8] bg-slate-100">
              <img
                src={img}
                alt={post.title}
                className="h-full w-full object-cover" loading="lazy" decoding="async" />
            </div>
          </div>
        )}

        {/* Body */}
        <article className="max-w-3xl mx-auto px-5 sm:px-8 mt-10">
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed font-medium mb-8 border-l-4 border-brand-blue pl-5 italic">
            {post.excerpt}
          </p>
          <PostBody blocks={post.content} />
        </article>

        {/* Inline CTA */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 mt-14">
          <div className="rounded-3xl bg-brand-navy text-white p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-300">
                Schedule a free pickup
              </p>
              <h3 className="mt-2 font-display text-xl sm:text-2xl font-bold">
                Door-to-door across the East Valley.
              </h3>
            </div>
            <Button
              asChild
              data-testid="blog-post-cta-schedule"
              className="rounded-full bg-amber-500 hover:bg-amber-400 text-brand-navy h-11 px-6 font-bold gap-2"
            >
              <Link to="/#schedule">
                Schedule pickup <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="max-w-5xl mx-auto px-5 sm:px-8 mt-20">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy">
              Keep reading
            </h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-5">
              {related.map((r) => {
                const ri = getPostImage(r);
                return (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    data-testid={`blog-related-${r.slug}`}
                    className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition-all"
                  >
                    <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                      {ri ? (
                        <img
                          src={ri}
                          alt={r.title}
                          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-brand-navy to-brand-blue" />
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-blue">
                        {r.category}
                      </p>
                      <h3 className="mt-1.5 font-display text-base font-bold text-brand-navy leading-tight group-hover:text-brand-blue transition-colors">
                        {r.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <MobileCTABar />
    </div>
  );
}
